import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import MessageIcon from "@mui/icons-material/Message";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ISampledPostItemComment } from "../../types/XSifterQueries";
import { IXSifterUser } from "../../types/XSifterAppUser";
import XSifterQueryService from "../../services/XSifterQueryService";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setNotesCollapsed } from "../../store/XSifterAppReducer";

const MAX_COMMENTS = 20;

interface IXSifterCodableItemNotesProps {
    itemId: string;
    codingRoundId: string;
    codedSampleId: string;
    savedQueryId: string | null,
    currentUser: IXSifterUser | undefined,
    onForceHeaderRemount: () => void
}

/**
 * Notes panel for a codable item. It fills its (positioned) parent, so the parent controls
 * the height, and the list of notes scrolls inside. Comment state lives here so that editing
 * notes never re-renders the tweet display.
 */
const XSifterCodableItemNotes: React.FC<IXSifterCodableItemNotesProps> = ({ codingRoundId, codedSampleId }) => {
    const dispatch = useAppDispatch();
    // Kept in the store (not the parent's state) so it survives item changes and toggling never
    // re-renders the tweet display, which would wipe its Twitter embed.
    const isCollapsed = useAppSelector((state) => state.app.notesCollapsed);
    const toggleCollapsed = () => dispatch(setNotesCollapsed(!isCollapsed));
    const [comments, setComments] = useState<ISampledPostItemComment[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isAddingComment, setIsAddingComment] = useState<boolean>(false);
    const [newCommentText, setNewCommentText] = useState<string>("");
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editingCommentText, setEditingCommentText] = useState<string>("");

    const queryService = new XSifterQueryService();

    queryService.setQueryTraceId(useAppSelector((state) => state.app.currentQueryId));

    const cancelNewComment = () => {
        setIsAddingComment(false);
        setNewCommentText("");
    };

    const errorText = (error: any, fallback: string) =>
        (error instanceof Error && error.message) ? error.message : fallback;

    useEffect(() => {
        let cancelled = false;
        queryService.getCommentsForSampledItem(codingRoundId, codedSampleId)
            .then((response) => {
                if (!cancelled) {
                    setComments(response);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    setErrorMessage(errorText(error, "Unable to load notes."));
                }
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codingRoundId, codedSampleId]);

    const isAtCommentLimit = comments.length >= MAX_COMMENTS;

    const addComment = () => {
        const text = newCommentText.trim();
        if (!text || isAtCommentLimit) {
            return;
        }
        setErrorMessage(null);
        queryService.postCommentForSampledItem(codingRoundId, codedSampleId, text)
            .then((created) => {
                setComments((prev) => [...prev, created]);
                cancelNewComment();
            })
            .catch((error) => setErrorMessage(errorText(error, "Unable to add note.")));
    };

    const startEditComment = (c: ISampledPostItemComment) => {
        setEditingCommentId(c.commentId);
        setEditingCommentText(c.comment);
    };

    const cancelEditComment = () => {
        setEditingCommentId(null);
        setEditingCommentText("");
    };

    const saveEditComment = () => {
        const text = editingCommentText.trim();
        if (!text) {
            return;
        }
        if (!editingCommentId) {
            return;
        }
        setErrorMessage(null);
        queryService.editCommentForSampledItem(codingRoundId, codedSampleId, editingCommentId, text)
            .then((updated) => {
                setComments((prev) => prev.map((c) => c.commentId === updated.commentId ? updated : c));
                cancelEditComment();
            })
            .catch((error) => setErrorMessage(errorText(error, "Unable to save note.")));
    };

    const deleteComment = (commentId: string) => {
        setErrorMessage(null);
        queryService.deleteCommentForSampledItem(codingRoundId, codedSampleId, commentId)
            .then(() => {
                setComments((prev) => prev.filter((c) => c.commentId !== commentId));
                if (editingCommentId === commentId) {
                    cancelEditComment();
                }
            })
            .catch((error) => setErrorMessage(errorText(error, "Unable to delete note.")));
    };

    return (
        <Box
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: isCollapsed ? "auto" : 0,
                display: "flex",
                flexDirection: "column",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                backgroundColor: "#ffffff",
                overflow: "hidden",
            }}
        >
            <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", px: 1.5, py: 0.5, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle2">Notes ({comments.length})</Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    {!isCollapsed && (
                        <Tooltip title={isAtCommentLimit ? `Limit of ${MAX_COMMENTS} notes reached` : "Add note"}>
                            <span>
                                <IconButton size="small" onClick={() => setIsAddingComment(true)} disabled={isAddingComment || isAtCommentLimit} aria-label="Add note">
                                    <MessageIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <Tooltip title={isCollapsed ? "Show notes" : "Hide notes"}>
                        <IconButton size="small" onClick={toggleCollapsed} aria-label={isCollapsed ? "Show notes" : "Hide notes"} aria-expanded={!isCollapsed}>
                            {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Stack>
            {!isCollapsed && (
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1 }}>
                {errorMessage && (
                    <Typography variant="caption" sx={{ color: "error.main", display: "block", mb: 1 }}>
                        {errorMessage}
                    </Typography>
                )}
                {isAddingComment && (
                    <Box sx={{ mb: 1 }}>
                        <TextField
                            autoFocus
                            multiline
                            fullWidth
                            minRows={2}
                            maxRows={5}
                            size="small"
                            placeholder="Enter a note..."
                            value={newCommentText}
                            onChange={(e) => setNewCommentText(e.target.value)}
                        />
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, justifyContent: "flex-end" }}>
                            <Button size="small" onClick={cancelNewComment}>Cancel</Button>
                            <Button size="small" variant="contained" onClick={addComment} disabled={!newCommentText.trim()}>Add</Button>
                        </Stack>
                    </Box>
                )}
                {comments.length === 0 && !isAddingComment && (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        No notes yet.
                    </Typography>
                )}
                {comments.map((c) => (
                    <Box key={c.commentId} sx={{ mb: 1, p: 1, borderRadius: 1, backgroundColor: "#f5f5f5" }}>
                        {editingCommentId === c.commentId ? (
                            <>
                                <TextField
                                    autoFocus
                                    multiline
                                    fullWidth
                                    minRows={2}
                                    maxRows={5}
                                    size="small"
                                    value={editingCommentText}
                                    onChange={(e) => setEditingCommentText(e.target.value)}
                                />
                                <Stack direction="row" spacing={1} sx={{ mt: 0.5, justifyContent: "flex-end" }}>
                                    <Button size="small" onClick={cancelEditComment}>Cancel</Button>
                                    <Button size="small" variant="contained" onClick={saveEditComment} disabled={!editingCommentText.trim()}>Save</Button>
                                </Stack>
                            </>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                    {c.comment}
                                </Typography>
                                <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        {new Date(c.commentTimestamp).toLocaleString()}
                                    </Typography>
                                    <Box>
                                        <Tooltip title="Edit note">
                                            <IconButton size="small" onClick={() => startEditComment(c)} aria-label="Edit note">
                                                <EditIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete note">
                                            <IconButton size="small" onClick={() => deleteComment(c.commentId)} aria-label="Delete note">
                                                <DeleteIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Stack>
                            </>
                        )}
                    </Box>
                ))}
            </Box>
            )}
        </Box>
    );
};

export default XSifterCodableItemNotes;
